import { FC, useCallback, useMemo } from "react"
import { CommissionCardItem } from "../commission-card/CommissionCardItem";
import { Sizes } from "components/forms/NumberSelector";
import { ContractFunnel } from "../../types/interfaces";
import { useAppDispatch } from "app/hooks";
import { updateContractFunnelItem } from "../../slices/pricing/pricingSlice";
import { FunnelCardPercentage } from "./FunnelCardPercentage";
import { usePanel } from "hooks/usePanel";
import { ContractGuarantees } from "../guarantees-card/ContractGuarantees";
import { useTranslation } from "react-i18next";
import { NsKey } from "enums/NsKey";
import { FormulaCardBody } from "../formulas/FormulaCardBody";
import { FormulaSubscriberBadge } from "../formulas/FormulaSubscriberBadge";

interface FunnelCardBodyProps {
  contractFunnel: ContractFunnel
  isProMode: boolean
}

export const FunnelCardBody: FC<FunnelCardBodyProps> = ({ contractFunnel, isProMode }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(NsKey.Pricing)
  const { openPanel } = usePanel();

  const onSelectGuarantee = useCallback(
    () => {
      openPanel({
        title: "Garanties",
        size: Sizes["4xl"],
      },
        <ContractGuarantees contractFunnel={contractFunnel} />
      )
    }, [openPanel, contractFunnel]
  );

  const matchScoreConfig = useMemo((): { matchScore: number, messageType: "success" | "warning" } => {
    if (!contractFunnel.matchScore) {
      return {
        matchScore: 100,
        messageType: 'success'
      }
    }
    return {
      matchScore: contractFunnel.matchScore,
      messageType: contractFunnel.matchScore < 100 ? "warning" : "success",
    }
  }, [contractFunnel.matchScore]);

  const handleFeeChange = useCallback((itemId: string) => {
    return (setValue: React.SetStateAction<number>, isAutoAdjustment?: boolean) => {
      // Si c'est un ajustement automatique, ne pas dispatcher
      const fee = contractFunnel.fees && contractFunnel.fees.find(fee => fee.code === itemId);
      const currentValue = fee ? fee.value : 0
      const newValue = typeof setValue === 'function'
        ? setValue(currentValue)
        : setValue;

      if (isAutoAdjustment) {
        return
      }

      // Dispatch la mise à jour du fee seulement si ce n'est pas un ajustement automatique
      dispatch(updateContractFunnelItem({
        contractFunnelId: contractFunnel.id,
        contractIdentifier: contractFunnel.contractIdentifier,
        itemId,
        type: 'fee',
        newValue,
      }));
    };
  }, [contractFunnel, dispatch]);

  const warningMessages = useMemo(() => {
    return [
      ...contractFunnel.guarantees.map(g => g.deductible?.warningMessage),
      ...contractFunnel.guarantees.map(g => g.ceiling?.warningMessage),
    ].filter((message): message is string => message !== undefined)
  }, [contractFunnel])

  // Hidden fees stay in the price and in what is sent back; only their line is not shown.
  const displayedFees = useMemo(
    () => (contractFunnel.fees ?? []).filter(fee => fee.displayedOnPricing !== false),
    [contractFunnel.fees]
  )

  // Formula: the title takes the whole line, its guarantees come below.
  const isFixedFormula = !!contractFunnel.product?.isFixedFormula

  return <div className="flex flex-col grow border-r">
    <div className={`flex gap-4 p-4 grow ${isFixedFormula ? 'flex-col justify-center' : 'items-center'}`}>
      <div className={isFixedFormula ? 'flex items-center justify-between gap-4' : ''}>
        <div>
          <p className="text-sm 2xl:text-base font-bold text-primary uppercase">
            Contrat {contractFunnel.supportLabel}
          </p>
          <p className="text-md 2xl:text-lg font-bold">
            {contractFunnel.name}
          </p>
          <p className="text-sm 2xl:text-base font-bold text-gray-400">
            {contractFunnel.contractIdentifier}
          </p>
        </div>
        {isFixedFormula && <FormulaSubscriberBadge contractFunnel={contractFunnel} />}
      </div>
      {/* Formula with fixed guarantees: its guarantees and its subscriber replace the score. */}
      {isFixedFormula
        ? <FormulaCardBody contractFunnel={contractFunnel} onSelectGuarantees={onSelectGuarantee} />
        : (
          <div className="flex relative items-center justify-end self-stretch grow">
            <FunnelCardPercentage
              onSelect={onSelectGuarantee}
              percentage={matchScoreConfig.matchScore}
              missingGuarantees={contractFunnel.missingGuarantees}
              warningGuaranteesMessages={warningMessages}
            />
          </div>
        )}
    </div>
    {
      isProMode === true &&
      <div className="flex border-t">
        <div className="flex justify-evenly gap-4 border-r grow w-full p-4">
          {displayedFees.map((fee) => (
            <CommissionCardItem
              size={Sizes.sm}
              key={`fee-funnel-body-${fee.code}`}
              title={fee.label}
              value={fee.value}
              onChange={handleFeeChange(fee.code)}
              warningMessage={fee.warningMessage}
              parameters={fee.parameters}
            />
          ))}
        </div>
        <div className={`p-4 flex flex-col items-center
          ${displayedFees.length === 1 && "w-full"}
        `}>
          <p className="flex text-nowrap text-xs">{t("PRICING.FUNNEL_CARD.BROKER_COMMISSION")}</p>
          <p className="flex text-nowrap text-sm font-bold text-center mt-1.5">{contractFunnel.pricing.brokerCommission} €</p>
        </div>
      </div>
    }
  </div>
}
